import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, Search, Loader2 } from "lucide-react";
import addressService from "@/services/addressService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * Modal para criar ou editar endereço
 */
export default function AddressFormModal({
  open,
  onOpenChange,
  address,
  onSave,
  loading,
  users = [],
}) {
  const isEditing = !!address;

  const [formData, setFormData] = useState({
    userId: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
    zipCode: "",
    isDefault: false,
  });

  const [errors, setErrors] = useState({});
  const [searchingCEP, setSearchingCEP] = useState(false);

  useEffect(() => {
    if (address) {
      setFormData({
        userId: address.userId || "",
        street: address.street || "",
        number: address.number || "",
        complement: address.complement || "",
        neighborhood: address.neighborhood || "",
        city: address.city || "",
        state: address.state || "",
        zipCode: address.zipCode || "",
        isDefault: address.isDefault || false,
      });
    } else {
      setFormData({
        userId: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        zipCode: "",
        isDefault: false,
      });
    }
    setErrors({});
  }, [address, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpar erro do campo ao digitar
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCEPChange = (value) => {
    // Allow only numbers and dash
    const cleanValue = value.replace(/[^\d-]/g, "");
    handleChange("zipCode", cleanValue);
  };

  const handleSearchCEP = async () => {
    const cep = formData.zipCode.replace(/\D/g, "");
    if (cep.length !== 8) {
      showError("CEP deve ter 8 dígitos", "CEP Inválido");
      return;
    }

    setSearchingCEP(true);
    try {
      const data = await addressService.searchByCEP(cep);
      setFormData((prev) => ({
        ...prev,
        street: data.street || prev.street,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: data.state || prev.state,
        zipCode: addressService.formatCEP(cep),
      }));
      showInfo("Endereço encontrado!", "CEP");
    } catch (error) {
      showError(error.message || "Erro ao buscar CEP", "Erro na Busca de CEP");
    } finally {
      setSearchingCEP(false);
    }
  };

  const validateForm = () => {
    const validation = addressService.validateAddressData(formData);
    setErrors(validation.errors);
    return validation.valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Preparar dados para envio
    const addressData = {
      ...formData,
      zipCode: formData.zipCode.replace(/\D/g, ""), // Remove formatting
    };

    onSave(addressData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Endereço" : "Novo Endereço"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Atualize as informações do endereço."
              : "Preencha os dados para criar um novo endereço."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Select */}
          {!isEditing && (
            <div className="space-y-2">
              <Label htmlFor="userId">
                Usuário <span className="text-red-500">*</span>
              </Label>
              <select
                id="userId"
                value={formData.userId}
                onChange={(e) => handleChange("userId", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.userId ? "border-red-500" : "border-gray-300"
                }`}
                disabled={loading}
              >
                <option value="">Selecione um usuário</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.email} (ID: {user.id})
                  </option>
                ))}
              </select>
              {errors.userId && (
                <div className="flex items-center gap-1 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.userId}</span>
                </div>
              )}
            </div>
          )}

          {/* CEP with Search */}
          <div className="space-y-2">
            <Label htmlFor="zipCode">
              CEP <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="zipCode"
                placeholder="12345-678"
                value={formData.zipCode}
                onChange={(e) => handleCEPChange(e.target.value)}
                className={errors.zipCode ? "border-red-500" : ""}
                disabled={loading}
                maxLength={9}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleSearchCEP}
                disabled={loading || searchingCEP}
              >
                {searchingCEP ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>
            {errors.zipCode && (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.zipCode}</span>
              </div>
            )}
            <p className="text-xs text-gray-500">
              Clique na lupa para buscar automaticamente
            </p>
          </div>

          {/* Street */}
          <div className="space-y-2">
            <Label htmlFor="street">
              Rua <span className="text-red-500">*</span>
            </Label>
            <Input
              id="street"
              placeholder="Rua das Flores"
              value={formData.street}
              onChange={(e) => handleChange("street", e.target.value)}
              className={errors.street ? "border-red-500" : ""}
              disabled={loading}
            />
            {errors.street && (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.street}</span>
              </div>
            )}
          </div>

          {/* Number and Complement */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="number">
                Número <span className="text-red-500">*</span>
              </Label>
              <Input
                id="number"
                placeholder="123"
                value={formData.number}
                onChange={(e) => handleChange("number", e.target.value)}
                className={errors.number ? "border-red-500" : ""}
                disabled={loading}
              />
              {errors.number && (
                <div className="flex items-center gap-1 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.number}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="complement">Complemento</Label>
              <Input
                id="complement"
                placeholder="Apto 101"
                value={formData.complement}
                onChange={(e) => handleChange("complement", e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Neighborhood */}
          <div className="space-y-2">
            <Label htmlFor="neighborhood">
              Bairro <span className="text-red-500">*</span>
            </Label>
            <Input
              id="neighborhood"
              placeholder="Centro"
              value={formData.neighborhood}
              onChange={(e) => handleChange("neighborhood", e.target.value)}
              className={errors.neighborhood ? "border-red-500" : ""}
              disabled={loading}
            />
            {errors.neighborhood && (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.neighborhood}</span>
              </div>
            )}
          </div>

          {/* City and State */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="city">
                Cidade <span className="text-red-500">*</span>
              </Label>
              <Input
                id="city"
                placeholder="São Paulo"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className={errors.city ? "border-red-500" : ""}
                disabled={loading}
              />
              {errors.city && (
                <div className="flex items-center gap-1 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.city}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">
                UF <span className="text-red-500">*</span>
              </Label>
              <Input
                id="state"
                placeholder="SP"
                value={formData.state}
                onChange={(e) =>
                  handleChange("state", e.target.value.toUpperCase())
                }
                className={errors.state ? "border-red-500" : ""}
                disabled={loading}
                maxLength={2}
              />
              {errors.state && (
                <div className="flex items-center gap-1 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.state}</span>
                </div>
              )}
            </div>
          </div>

          {/* Default Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isDefault"
              checked={formData.isDefault}
              onCheckedChange={(checked) => handleChange("isDefault", checked)}
              disabled={loading}
            />
            <Label
              htmlFor="isDefault"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Definir como endereço padrão
            </Label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{ background: "#c9332C" }}
              onMouseEnter={(e) =>
                !loading && (e.target.style.background = "#a02820")
              }
              onMouseLeave={(e) =>
                !loading && (e.target.style.background = "#c9332C")
              }
            >
              {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
